export const fr = {
  nav: {
    model: "Notre modèle",
    invest: "Investir",
    simulator: "Simulateur",
    risks: "Risques & conformité",
    faq: "FAQ",
    login: "Connexion",
    signup: "Créer un compte",
    dashboard: "Espace investisseur",
    logout: "Se déconnecter",
    admin: "Admin",
  },
  hero: {
    eyebrow: "Infrastructure GPU souveraine · Europe",
    title: "Investir dans l'infrastructure GPU souveraine à partir de 1 000€",
    subtitle:
      "Accès à des parcs GPU opérés pour des acteurs régulés — santé, assurance, médical, IA souveraine — via des SPV dédiés, contrats long terme et transparence opérationnelle.",
    cta_primary: "Simuler mon investissement",
    cta_secondary: "Accéder à la documentation",
    proofs: [
      { k: "Actifs réels", v: "GPU H100 / A100 / H200" },
      { k: "Contrats", v: "5 ans, clients régulés" },
      { k: "Transparence", v: "Reporting mensuel + télémétrie" },
    ],
  },
  warning: {
    short:
      "Rendement cible non garanti. Capital non garanti, risque de perte partielle ou totale. Coupons semestriels capitalisés sur compte interne InvestGPU, liquidité au terme du contrat ou via cession sur marketplace.",
  },
  home: {
    why_now_title: "Pourquoi maintenant",
    why_now_body:
      "L'Europe manque de capacité GPU souveraine pour les acteurs régulés. Nous finançons, exploitons et capitalisons les revenus générés par des parcs localisés EU, sous contrats cadres 5 ans, sous forme de coupons semestriels.",
    sovereignty_title: "Pourquoi la souveraineté compte",
    sovereignty_body:
      "Les acteurs régulés (santé, assurance, IA sous AI Act) ont besoin d'infrastructures situées en UE, tracées et opérées par des acteurs européens. Cette contrainte crée une demande structurelle.",
    revenue_title: "Comment les revenus sont générés",
    revenue_body:
      "Chaque SPV détient un parc GPU amorti sur 36 mois, hébergé dans un datacenter partenaire, et loué à un ou plusieurs clients régulés sous contrat. Les loyers, après waterfall, sont versés tous les six mois sous forme de coupons crédités sur votre compte de capitalisation InvestGPU.",
    industries_title: "Industries cibles",
  },
  model: {
    title: "Notre modèle économique",
    subtitle: "Du véhicule juridique au compte de capitalisation investisseur.",
    steps: [
      { n: "01", t: "SPV dédié", d: "Un véhicule par millésime / client, pour cloisonner les risques." },
      { n: "02", t: "Acquisition GPU", d: "Hardware sélectionné (H100, A100, H200) adapté à l'usage cible." },
      { n: "03", t: "Hébergement EU", d: "Datacenter partenaire Tier III+ localisé en France ou UE." },
      { n: "04", t: "Location long terme", d: "Contrats 5 ans avec clients régulés. Taux d'occupation piloté." },
      { n: "05", t: "Revenus", d: "Loyers GPU récurrents, ajustés énergie/usage." },
      { n: "06", t: "Coupons semestriels", d: "Versement semestriel sur votre compte de capitalisation InvestGPU, dédié au réinvestissement." },
    ],
    do: ["Financer des actifs GPU", "Opérer un reporting mensuel", "Cloisonner par SPV", "Capitaliser via coupons semestriels"],
    dont: ["Promettre un rendement garanti", "Offrir de la liquidité quotidienne", "Mélanger les actifs entre SPV", "Spéculer sur la revente court terme"],
  },
  invest: {
    title: "Investir via un SPV",
    subtitle: "Ticket minimum 1 000€ — horizon 3 ans — rendement cible 12%/an, capitalisé via coupons semestriels.",
    scenarios_title: "Scénarios de rendement",
    scenarios: [
      {
        key: "prudent",
        name: "Prudent",
        yield: "8%",
        body: "Occupation ~70%, conditions marché dégradées, renégociation partielle.",
      },
      {
        key: "central",
        name: "Central",
        yield: "12%",
        body: "Occupation ~85%, contrats nominaux, amortissement 36 mois.",
      },
      {
        key: "ambitious",
        name: "Ambitieux",
        yield: "15%",
        body: "Occupation >92%, tension sur la demande GPU, pricing premium.",
      },
    ],
    invest_cta: "Souscrire",
    min: "Ticket min",
    horizon: "Horizon",
    target: "Rendement cible",
    raised: "Collecté",
    total: "Objectif",
  },
  simulator: {
    title: "Simulateur d'investissement",
    subtitle: "Projection informative sur la base d'un scénario. Coupons semestriels capitalisés. Non contractuel.",
    amount: "Montant investi",
    horizon: "Durée (mois)",
    scenario: "Scénario de rendement",
    projected_return: "Rendement total estimé",
    monthly: "Coupon semestriel estimé",
    annual: "Rendement annuel cible",
    cta: "Créer mon compte investisseur",
  },
  risks: {
    title: "Risques et conformité",
    subtitle: "Comprendre avant d'investir.",
    items: [
      { t: "Risque de perte en capital", d: "Le capital n'est pas garanti. Une perte partielle ou totale reste possible." },
      { t: "Risque technique", d: "Défaillance hardware, indisponibilité datacenter, obsolescence GPU." },
      { t: "Risque d'occupation", d: "Baisse du taux de location des GPU, renégociation des loyers." },
      { t: "Risque de contrepartie", d: "Défaut d'un client locataire principal." },
      { t: "Risque réglementaire", d: "Évolution AI Act, DORA, régulation datacenter." },
      { t: "Risque de liquidité", d: "Les coupons capitalisés ne deviennent retirables qu'au terme du contrat ou via une cession encadrée sur la marketplace InvestGPU. Aucune sortie anticipée n'est garantie." },
    ],
  },
  faq: {
    title: "Questions fréquentes",
    items: [
      {
        q: "Quel est le ticket minimum ?",
        a: "Le ticket minimum d'entrée est de 1 000€ par SPV. Il peut être augmenté sous validation du profil.",
      },
      {
        q: "Quelle est la durée de blocage ?",
        a: "L'horizon cible est de 36 mois. Avant cette échéance, aucune sortie n'est garantie. Une cession encadrée peut être demandée selon les conditions contractuelles.",
      },
      {
        q: "Le rendement est-il garanti ?",
        a: "Non. Nous communiquons un rendement cible dans le scénario central. Le rendement effectif dépend de l'occupation réelle, des loyers perçus et des coûts d'exploitation.",
      },
      {
        q: "Comment et quand suis-je rémunéré ?",
        a: "Les revenus nets de votre SPV sont versés sous forme de coupons semestriels (deux fois par an, après waterfall) crédités sur votre compte de capitalisation interne InvestGPU.",
      },
      {
        q: "Qu'est-ce que le compte de capitalisation InvestGPU ?",
        a: "Un compte interne sécurisé, propre à chaque investisseur, qui reçoit les coupons semestriels. Le solde est exclusivement destiné au réinvestissement dans un autre SPV InvestGPU. Il n'est pas retirable en numéraire avant le terme du contrat.",
      },
      {
        q: "Quand les fonds deviennent-ils liquides ?",
        a: "Le solde de votre compte de capitalisation devient retirable uniquement (i) à l'échéance contractuelle du SPV (typiquement 36 mois) ou (ii) en cas de cession encadrée de vos parts sur la marketplace secondaire InvestGPU. Aucune sortie anticipée n'est garantie.",
      },
      {
        q: "Comment suis-je informé ?",
        a: "Chaque mois, un reporting opérationnel vous est mis à disposition dans votre espace investisseur (télémétrie, KPI SPV, événements notables). Le versement effectif des coupons est lui semestriel.",
      },
      {
        q: "Suis-je éligible à l'investissement ?",
        a: "Un questionnaire de qualification est obligatoire avant toute souscription, conformément aux exigences réglementaires européennes.",
      },
      {
        q: "Quelle fiscalité s'applique ?",
        a: "La fiscalité dépend de votre pays de résidence et de votre statut. Un récapitulatif fiscal annuel est mis à disposition dans votre espace.",
      },
    ],
  },
  auth: {
    login_title: "Connexion",
    signup_title: "Créer votre espace investisseur",
    email: "E-mail",
    password: "Mot de passe",
    name: "Nom complet",
    submit_login: "Se connecter",
    submit_signup: "Créer le compte",
    google: "Continuer avec Google",
    or: "ou",
    have_account: "Déjà inscrit ?",
    no_account: "Pas encore de compte ?",
    terms: "J'accepte les CGU et la politique RGPD.",
  },
  qualification: {
    title: "Qualification investisseur",
    subtitle: "Questionnaire obligatoire avant toute souscription.",
    country: "Pays de résidence",
    investor_type: "Type d'investisseur",
    experience: "Années d'expérience en investissement",
    net_worth: "Patrimoine financier",
    risk_tolerance: "Tolérance au risque",
    horizon_accepted: "J'accepte une durée de blocage de 36 mois.",
    loss_accepted: "Je comprends que je peux perdre tout ou partie du capital investi.",
    liquidity_accepted: "J'accepte que la liquidité soit limitée voire nulle avant l'échéance.",
    pep_status: "Je suis une Personne Politiquement Exposée (PPE).",
    source_of_funds: "Origine des fonds",
    submit: "Valider ma qualification",
  },
  dashboard: {
    welcome: "Bonjour",
    value_invested: "Capital engagé",
    total_distributed: "Solde compte de capitalisation",
    target_yield: "Rendement cible",
    next_distribution: "Prochain coupon semestriel estimé",
    active_spvs: "SPV actifs",
    gpu_live: "Activité GPU · Live",
    occupancy: "Taux d'occupation",
    uptime: "Uptime",
    power: "Puissance",
    gpus_active: "GPU actifs",
    daily_revenue: "Revenu journalier",
    co2_saved: "CO₂ évité (est.)",
    performance: "Performance par SPV",
    documents: "Documents",
    subscribe_new: "Souscrire à un nouveau SPV",
    coupon_account_title: "Compte de capitalisation InvestGPU",
    coupon_account_desc: "Vos coupons sont versés tous les 6 mois sur ce compte interne. Le solde est exclusivement destiné au réinvestissement dans un autre SPV InvestGPU. La liquidité (retrait en numéraire) intervient uniquement à l'échéance contractuelle du SPV ou en cas de cession encadrée sur la marketplace secondaire.",
    coupon_balance: "Solde capitalisé",
    coupon_next: "Prochain coupon estimé",
    coupon_next_date: "Date prévisionnelle",
    coupon_locked_label: "Bloqué jusqu'au terme du contrat",
    coupon_action_reinvest: "Réinvestir",
    coupon_action_marketplace: "Voir la marketplace (à venir)",
  },
  footer: {
    rights: "Tous droits réservés",
    legal: "Mentions légales",
    privacy: "Politique de confidentialité",
    disclaimer:
      "InvestGPU est une plateforme d'investissement dans l'infrastructure GPU opérée via SPV dédiés. Les offres ne sont pas garanties par l'État. Le capital investi présente un risque de perte partielle ou totale. L'accès à l'investissement est soumis à validation du dossier. Certaines fonctionnalités peuvent être limitées selon la juridiction de résidence. Les performances passées ou simulées ne préjugent pas des performances futures.",
  },
};
